import React, { useMemo } from 'react'
import { useSplitClient } from 'contrastBase/state'
import { hasRole } from 'contrastBase/RoleSwitch'
import { RouteContext } from '../../../../components/route-provider'
import { transformRole } from '../../../../graphql/schema/queries/helpers/role'
import { useOrganizationProfile } from '../../../../components/session-metadata/api'
import { userHasResourceAction } from 'contrastBase/accessControl'

type ReturnType = {
  /** Combined checks for static view permissions. */
  canViewStatic: boolean
}

/**
 * Determines if the user has permission to view the static libraries and route.
 * - Rbac
 *     - Users who don't have the feature flag and have SCA_PROJECT_GROUP_VIEW *can view* the static libraries.
 *     - Users who don't have the feature flag and don't have SCA_PROJECT_GROUP_VIEW *cannot view* the static libraries.
 * - Non-Rbac
 *     - Users who don't have the feature flag *can view* the static libraries, regardless of org permissions.
 *     - Users who have the feature flag and are org admins *can view* the static libraries.
 *     - Users who have the feature flag and are not org admins *cannot view* the static libraries.
 *     - See more - https://contrast.atlassian.net/wiki/spaces/TS/pages/3362553976/Omnicom+Static+SCA+Projects+UI+Notes
 */
export const useStaticView = (): ReturnType => {
  const { isSplitTreatmentOn } = useSplitClient()
  const { organizationID } = React.useContext(RouteContext)
  const { data: organizationProfile } = useOrganizationProfile({
    organizationUuid: organizationID as string
  })

  const organizationRole = useMemo(
    () => transformRole(organizationProfile?.roles),
    [organizationProfile]
  )

  const isRbacScaAuthorized = isSplitTreatmentOn('SCA-1768_rbac')
    ? userHasResourceAction('SCA_PROJECT_GROUP', 'SCA_PROJECT_GROUP_VIEW')
    : true

  const hasStaticViewPermission = isSplitTreatmentOn(
    'PROD-2918_hide_sca_data_unless_org_admin'
  )
    ? !!organizationRole && hasRole(organizationRole, 'ADMIN')
    : true

  return {
    canViewStatic: hasStaticViewPermission && isRbacScaAuthorized
  }
}
